import {createPreparedPrompt} from './contracts'
import type {
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpDirectTurnTelemetry,
  FirstTurnHandoff,
  PreparedPrompt,
  StopOutcome,
} from './contracts'

export interface InitialAhpSubmissionRequest {
  readonly prompt: PreparedPrompt
  readonly origin: FirstTurnHandoff['origin']
  /** Reuse this id only when the caller is deliberately retrying one request. */
  readonly requestId?: string
  readonly metadata?: Readonly<Record<string, unknown>>
}

export interface EstablishedAhpInitialSubmissionCoordinator {
  prepare?(): void
  submit(request: InitialAhpSubmissionRequest): Promise<EstablishedAhpSession>
  cancel?(): StopOutcome
  dispose?(): void
}

export interface ManagedEstablishedAhpInitialSubmissionCoordinator extends EstablishedAhpInitialSubmissionCoordinator {
  activate(): void
  deactivate(): void
  prepare(): void
  cancel(): StopOutcome
  dispose(): void
}

export interface EstablishedAhpInitialSubmissionOptions {
  readonly bootstrap: EstablishedAhpSessionBootstrap
  readonly identity?: EstablishedAhpSessionIdentity
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
}

/**
 * Coordinates the one first-turn path shared by product bootstraps.
 *
 * The coordinator delegates acceptance, lifecycle, and cleanup to the bootstrap.
 * It never retries an ambiguous or rejected acknowledgement; callers must invoke
 * submit again explicitly when the user retries.
 */
export function createEstablishedAhpInitialSubmissionCoordinator({
  bootstrap,
  identity,
  directTurnTelemetry = bootstrap.directTurnTelemetry,
}: EstablishedAhpInitialSubmissionOptions): ManagedEstablishedAhpInitialSubmissionCoordinator {
  let active = true
  let disposed = false
  const prepareRecovery = async (recoveryIdentity: EstablishedAhpSessionIdentity) => {
    try {
      await bootstrap.establish({mode: 'recover', identity: recoveryIdentity})
    } catch {
      // Preparation is speculative; Submit owns user-visible retry and error handling.
    }
  }

  return {
    activate: () => {
      if (!disposed) active = true
    },
    deactivate: () => {
      active = false
    },
    prepare: () => {
      if (!active || disposed || !identity) return
      void prepareRecovery(identity)
    },
    cancel: () => {
      if (!active || disposed) return {accepted: false}
      return bootstrap.cancelPendingFirstTurn()
    },
    submit: async ({prompt, origin, requestId = crypto.randomUUID(), metadata}) => {
      if (disposed) throw new Error('AHP initial submission coordinator is disposed.')
      if (!active) throw new Error('AHP initial submission coordinator is inactive.')
      const firstTurn: FirstTurnHandoff = {requestId, prompt: createPreparedPrompt(prompt), origin}
      directTurnTelemetry?.beginSubmission({
        requestId,
        turnKind: identity ? 'existing_chat' : 'new_chat',
        startPath: identity ? 'resume' : 'cold',
        ...(firstTurn.prompt.model?.id ? {modelId: firstTurn.prompt.model.id} : {}),
      })
      try {
        return await (identity
          ? bootstrap.establish({mode: 'recover', identity, firstTurn})
          : bootstrap.establish({mode: 'provision', input: {firstTurn, ...(metadata ? {metadata} : {})}}))
      } catch (error) {
        directTurnTelemetry?.preDispatchError(requestId)
        throw error
      }
    },
    dispose: () => {
      active = false
      disposed = true
    },
  }
}
